import { Component, computed, effect, inject, model, untracked } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatMenuModule } from '@angular/material/menu';
import { filter } from 'rxjs';

import { FileVersionCommentModel } from '@files/attachments-note/attachments-note/attachments-note.model';
import { MyDriveFileModel } from '@models/files/mydrive-file.model';
import { ProjectFileModel } from '@models/files/project-file.model';
import { AttachmentModel, PreviewableMimeTypes } from '@models/module/fields/main';
import { SystemModulesEnum } from '@models/module/main/systemModules.enum';
import {
  FolderTreeDialogComponent,
  FolderTreeDialogData,
} from '@module-definition/folder-tree/folder-tree-dialog/folder-tree-dialog.component';
import {
  AttachmentsDialogComponent,
  AttachmentsDialogData,
} from '@module-instance/attachments-dialog/attachments-dialog.component';
import {
  FileFactoryService,
  FileServiceType,
  IFileService,
} from '@module-instance/my-drive/common/services/file-factory.service';
import { CipoControlBase } from '@shared/components/fields/cipo-control.base';
import { CipoAttachmentConfig } from '@shared/components/fields/common';
import { InfoDialog } from '@shared/dialogs';
import { CipoFormModule } from '@shared/modules/forms.module';
import { ImageSourcePipe } from '@shared/pipes/string-pipes/image-source.pipe';
import { FileService, UserService } from '@shared/services';
import { UserStore } from '@signal-store/index';
import { CipoOutlineFieldComponent } from '../cipo-outline-field/cipo-outline-field.component';
import {
  AttachmentSelectDialogComponent,
  AttachmentSelectDialogData,
  AttachmentSelectDialogResult,
} from './attachment-select-dialog/attachment-select-dialog.component';

@Component({
  selector: 'cipo-attachment',
  imports: [CipoFormModule, MatMenuModule, ImageSourcePipe, CipoOutlineFieldComponent],
  providers: [FileFactoryService],
  templateUrl: './cipo-attachment.component.html',
})
export class CipoAttachmentComponent extends CipoControlBase<
  AttachmentModel[] | AttachmentModel,
  CipoAttachmentConfig
> {
  private dialog = inject(MatDialog);
  private userService = inject(UserService);
  private fileFactoryService = inject(FileFactoryService);
  private userStore = inject(UserStore);
  private apiFileService: IFileService<Partial<ProjectFileModel> & Partial<MyDriveFileModel>>;
  private fileService = inject(FileService);

  internalControl = new FormControl<AttachmentModel[]>(null);
  driveModuleName = computed(
    () =>
      this.userStore.menuItemsList()?.find(item => item.moduleId === SystemModulesEnum.MyCIPODrive)?.name || 'My drive',
  );
  projectFilesModule = computed(() =>
    this.userStore.menuItemsList()?.find(item => item.moduleId === SystemModulesEnum.FileContractManager),
  );
  projectFilesModuleName = computed(() => this.projectFilesModule()?.name || 'Project files');
  private projectFilesContext = computed(
    () => this.userStore.contract() ?? this.userStore.project() ?? this.userStore.program(),
  );

  hasAttachments = computed(() => !!this.attachments()?.length);

  /** For view mode, to use without formControl */
  attachments = model<AttachmentModel[]>(null);

  constructor() {
    super();
    this.apiFileService = this.fileFactoryService.getServiceType(FileServiceType.MyDrive);

    effect(() => {
      const control = this.control();
      if (!control) {
        return;
      }
      const formValue = this.formValue();
      const multiple = untracked(() => this.fieldData()?.multiple);
      const attachmentsArray = (multiple ? formValue : formValue ? [formValue] : null) as AttachmentModel[];
      this.internalControl.setValue(attachmentsArray);
      if (control.touched) {
        this.internalControl.markAsTouched();
        this.internalControl.updateValueAndValidity();
      }
      this.attachments.set(attachmentsArray ?? []);
    });

    effect(() => {
      if (typeof this.deepFieldData.required() === 'boolean') {
        this.updateValidators(this.internalControl);
      }
    });

    effect(() => {
      const touched = this.controlTouched();
      if (touched) {
        this.internalControl.markAsTouched();
      } else {
        this.internalControl.markAsUntouched();
      }
    });
  }

  canDisplayInBrowser(item: AttachmentModel): boolean {
    return PreviewableMimeTypes.includes(item.mimeType);
  }

  removeChip(index: number) {
    if (this.control()) {
      if (!this.fieldData()?.multiple) {
        this.control().setValue(null);
        this.control().markAsTouched();
        return;
      }
      const chipsLeft = this.internalControl.value?.filter((_, i) => i !== index);
      this.control().setValue(chipsLeft);
      this.control().markAsTouched();
    } else {
      const chipsLeft = this.attachments().filter((_, i) => i !== index);
      this.attachments.set(chipsLeft);
    }
  }

  addAttachments() {
    this.dialog
      .open<AttachmentsDialogComponent, AttachmentsDialogData, AttachmentModel[]>(AttachmentsDialogComponent, {
        panelClass: ['cipo-dialog', 'classic', 'cipo--height--full'],
        ...this.userService.getResponsiveDialogSize().lg,
        data: {
          attachments: this.attachments(),
          multiple: this.fieldData()?.multiple,
        },
      })
      .afterClosed()
      .pipe(filter(result => !!result))
      .subscribe(attachments => {
        if (this.control()) {
          this.control().setValue(this.fieldData().multiple ? attachments : attachments[0]);
          this.control().markAsTouched();
        } else {
          this.attachments.set(attachments);
        }
      });
  }

  openNewTab(item: AttachmentModel) {
    this.apiFileService.getFileUrl(item.id, item.versionId, true).subscribe(url => window.open(url, '_blank'));
  }

  download(item: AttachmentModel) {
    this.apiFileService.getFileUrl(item.id, item.versionId).subscribe(url => {
      this.apiFileService.downloadFileFromUrl(url, item.name);
    });
  }

  downloadAll() {
    const attachments = this.attachments();
    if (!attachments || !attachments.length) {
      return;
    }
    this.apiFileService.getFilesUrl(attachments.map(f => f.versionId)).subscribe(files => {
      files.forEach(({ url, versionId }, index) => {
        const attachment = attachments.find(a => a.versionId === versionId);
        setTimeout(() => this.apiFileService.downloadFileFromUrl(url, attachment?.name), index * 700);
      });
    });
  }

  openFile(index: number, event: MouseEvent) {
    event.stopPropagation();
    // WI-8965: files opened from an attachment field on a CIPO document must be read-only
    // to prevent unauthorised edits. The field's own readonly flag captures this intent.
    // If the file is opened from a field in a draft document, however, it should be editable, so we cannot rely on the readonly flag for that.
    const readOnly = !!this.fieldData()?.readonly || this.fieldData()?.isDraft === false;
    this.fileService.openFile(this.attachments(), index, readOnly);
  }

  saveFileTo(item: AttachmentModel, location: 'drive' | 'projectFiles' = 'drive') {
    this.dialog.open<FolderTreeDialogComponent, FolderTreeDialogData>(FolderTreeDialogComponent, {
      panelClass: ['cipo-dialog', 'classic', 'cipo--height--full'],
      ...this.userService.getResponsiveDialogSize().md,
      data: {
        entityInstanceId: location === 'projectFiles' ? this.projectFilesContext()?.entityInstanceId : null,
        rootModuleId: location === 'projectFiles' ? this.projectFilesContext()?.moduleId : null,
        files: [item],
      },
    });
  }

  openDownloadDialog(): void {
    this.openAttachmentSelectDialog('cipoFields.selectAttachmentsToDownload', 'cipoFields.download').subscribe(
      result => {
        if (result?.selectedIds?.length) {
          const selected = this.attachments().filter(a => result.selectedIds.includes(a.id));
          this.apiFileService.getFilesUrl(selected.map(f => f.versionId)).subscribe(files => {
            files.forEach(({ url, versionId }, index) => {
              const attachment = selected.find(a => a.versionId === versionId);
              setTimeout(() => this.apiFileService.downloadFileFromUrl(url, attachment?.name), index * 700);
            });
          });
        }
      },
    );
  }

  openSaveToDialog(location: 'drive' | 'projectFiles' = 'drive'): void {
    this.openAttachmentSelectDialog('cipoFields.selectAttachmentsToSave', 'general.save').subscribe(result => {
      if (result?.selectedIds?.length) {
        const selected = this.attachments().filter(a => result.selectedIds.includes(a.id));
        this.dialog.open<FolderTreeDialogComponent, FolderTreeDialogData>(FolderTreeDialogComponent, {
          panelClass: ['cipo-dialog', 'classic', 'cipo--height--full'],
          ...this.userService.getResponsiveDialogSize().md,
          data: {
            entityInstanceId: location === 'projectFiles' ? this.projectFilesContext()?.entityInstanceId : null,
            rootModuleId: location === 'projectFiles' ? this.projectFilesContext()?.moduleId : null,
            files: selected,
          },
        });
      }
    });
  }

  private openAttachmentSelectDialog(title: string, confirmLabel: string) {
    const attachments = this.attachments();
    const totalBytes = attachments.reduce((sum, a) => sum + (a.length || 0), 0);
    return this.dialog
      .open<AttachmentSelectDialogComponent, AttachmentSelectDialogData, AttachmentSelectDialogResult>(
        AttachmentSelectDialogComponent,
        {
          panelClass: ['cipo-dialog', 'classic'],
          ...this.userService.getResponsiveDialogSize().sm,
          data: {
            title,
            confirmLabel,
            totalSize: totalBytes ? this.formatBytes(totalBytes) : undefined,
            items: attachments.map(a => ({ id: a.id, name: a.name, selected: false })),
          },
        },
      )
      .afterClosed()
      .pipe(filter(result => !!result));
  }

  private formatBytes(bytes: number): string {
    if (bytes === 0) {
      return '0 B';
    }
    const units = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return parseFloat((bytes / Math.pow(1024, i)).toFixed(2)) + ' ' + units[i];
  }

  onAddAttachmentNote(): void {
    const attachments = this.attachments();
    if (!attachments?.length) {
      return;
    }

    import('./attachment-note-dialog/attachment-note-dialog.component').then(m => {
      const fd = this.fieldData();
      this.dialog
        .open(m.AttachmentNoteDialogComponent, {
          panelClass: ['cipo', 'cipo-dialog', 'classic', 'cipo--height--full'],
          ...this.userService.getResponsiveDialogSize().md,
          disableClose: true,
          data: {
            attachments: [...attachments],
            entityInstanceId: fd?.entityInstanceId || 0,
            fieldId: fd?.id || 0,
            parentEntityInstanceId: fd?.contractId || 0,
            onlyView: !!fd?.readonly,
            isDraft: fd?.isDraft ?? false,
          },
        })
        .afterClosed()
        .subscribe((result: FileVersionCommentModel[]) => {
          if (result) {
            let hasChanges = false;
            const updatedAttachments = attachments.map(att => {
              const noteData = result.find(r => r.fileId === att.id);
              if (noteData) {
                const currentNote = att.note || '';
                const newNote = noteData.comment || '';
                if (currentNote !== newNote) {
                  hasChanges = true;
                  return { ...att, note: newNote };
                }
              }
              return att;
            });

            if (hasChanges) {
              if (this.control()) {
                this.control().setValue(this.fieldData()?.multiple ? updatedAttachments : updatedAttachments[0]);
                this.control().markAsTouched();
              } else {
                this.attachments.set(updatedAttachments);
              }
            }
          }
        });
    });
  }

  openNote(item: AttachmentModel): void {
    this.dialog.open(InfoDialog, {
      panelClass: ['cipo-dialog', 'classic'],
      ...this.userService.getResponsiveDialogSize().xs,
      data: {
        title: item.name,
        description: item.note || '',
      },
    });
  }
}
